import { useEffect, useState, useCallback } from "react";
import { CategoriesHeader } from "../components/Categories/CategoriesHeader";
import { CategoriesList } from "../components/Categories/CategoriesList";
import { CategoriesForm } from "../components/Categories/CategoriesForm"; // Fixed typo in import
import { Card } from "../../components/ui/card";
import {
  createCategory,
  deleteCategory,
  getAllCategories,
  updateCategory,
} from "../api/adminCategoryApi";

const Categories = () => {
  // State management
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    status: true, // Changed from 'active' to 'status' to match backend schema
  });

  // Fetch categories with better error handling
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getAllCategories();
      console.log("Category Api result", result);

      if (result.success) {
        setCategories(result.categories);
      } else {
        setError(`Failed to fetch categories: ${result.error}`);
        setCategories([]); // Set empty array on error
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError(`Failed to fetch caetgories: ${error.message}`);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Computed values - with null safety
  const filteredCategories = categories.filter(
    (category) =>
      category?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ?? false
  );

  // Form reset
  const resetForm = useCallback(() => {
    setFormData({ name: "", status: true });
    setEditingCategory(null);
    setIsDialogOpen(false);
    setIsSubmitting(false);
  }, []);

  // Handle submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingCategory) {
        // Update existing category
        console.log("Updating category...");
        const updatedCategory = await updateCategory(
          editingCategory._id,
          formData
        );
        console.log("Update category result:", updatedCategory);
        if (updatedCategory.success) {
          await fetchCategories();

          setFormData({
            name: "",
            status: true,
          });
          console.log("Category updated successfully");
        } else {
          setError(updatedCategory.error);
          console.error("Failed to update category:", updatedCategory.error);
          return; // Don't close dialog on error
        }
      } else {
        // Create new  category
        const result = await createCategory(formData);
        console.log("Create category result:", result);

        if (result.success) {
          await fetchCategories();

          setFormData({
            name: "",
            status: true,
          });
        } else {
          setError(result.error);
          console.error("Failed to create category:", error);
          return; // Don't close dialog on error
        }
      }

      setIsDialogOpen(false);
      setEditingCategory(null);
    } catch (error) {
      setError(error.message || "An unexpexted error occurred");
      console.log("Error submitting form", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || "",
      status: category.status ?? true,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    try {
      const confirmed = window.confirm("Do you wish to delete this location?")
      if (!confirmed) return

      await deleteCategory(categoryId)

      await fetchCategories()
    } catch (error) {
      setError(error.message)
      console.error("Error deleting category:", error);
    }
  }

  // Handle dialog close
  const handleDialogClose = () => {
    resetForm();
    setError(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <Card>
        {/* Header with search and add button */}
        <CategoriesHeader
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setEditingCategory={setEditingCategory}
          formData={formData}
          setFormData={setFormData}
          editingCategory={editingCategory}
          filteredCategories={filteredCategories}
          onRefresh={fetchCategories}
        />

        <CategoriesList
          filteredCategories={filteredCategories}
          // toggleStatus={toggleStatus}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          loading={loading}
        />
      </Card>

      <CategoriesForm
        editingCategory={editingCategory}
        formData={formData}
        setFormData={setFormData}
        setIsDialogOpen={setIsDialogOpen}
        isDialogOpen={isDialogOpen}
        handleSubmit={handleSubmit}
        onClose={handleDialogClose}
        isSubmitting={isSubmitting}
        error={error}
        setError={setError}
      />
    </div>
  );
};

export default Categories;
